<template>
  <main>
    <article class="HeroImage">
      <div class="CenterHeading">
        <div class="HeadingContainer">
          <h1 class="PageTitle">{{ $t('faq_screen.small_header') }}</h1>
          <h3 class="Question">{{ $t('faq_screen.big_header') }}</h3>
        </div>
      </div>
      <div class="Flex">
        <div class="FlexGrow">
          <div
            class="Bullet"
            @click="scrollToEl(idx)"
            v-for="(bullet, idx) in bullets"
            :key="idx"
          >
            <a :key="idx">{{ $t(bullet) }}</a>
          </div>
        </div>
      </div>
    </article>
    <div class="FAQContainer">
      <div class="CenterFAQ">
        <Space :pheight="pageTopSpacing" />
        <template v-for="(section, idx) in faqs">
          <div :key="section.title" :ref="idx">
            <Space :pheight="idx === 0 ? '44px' : '99px'" />
            <div class="SectionTitle">{{ section.title }}</div>
            <div class="Line"></div>
            <QandA
              v-for="(data, idx) in section.values"
              :question="data.q"
              :answer="data.a"
              :isImage="data.type === 'image'"
              :isList="data.type === 'list'"
              :metaData="data.metaData"
              :analyticsPermitted="analyticsPermitted"
              :key="idx"
            />
          </div>
        </template>
      </div>
    </div>
  </main>
</template>

<script>
import Space from '../layout/space';
import QandA from '../components/FAQPage/QandA.vue';
import { cookiesKey } from '../shared/analytics';
import { getBulletsByEntity } from '../shared/entitySpecificData';

export default {
  name: 'FAQPage',
  components: {
    Space,
    QandA,
  },
  data() {
    return {
      open: false,
      // FIXME(greg): this needs bullet proofing. Right now
      // the faq titles are hard coded. Ideally they wouldn't be
      // but this is a quick fix for now
      bullets: getBulletsByEntity(),

      pageTopSpacing: '99px',
      analyticsPermitted: false,
      faqs: [],
    };
  },
  created() {
    const keys = Object.keys(this.$t('FAQs'));
    this.faqs = keys
      .map(key => this.$t(`FAQs.${key}`))
      .sort((a, b) => a.ranking - b.ranking);
  },
  methods: {
    onResize() {
      if (window.innerWidth < 768 && this.pageTopSpacing !== '0px') {
        this.pageTopSpacing = '0px';
      } else if (window.innerWidth > 768 && this.pageTopSpacing !== '99px') {
        this.pageTopSpacing = '99px';
      }
    },
    scrollToEl(id) {
      const element = this.$refs[id][0];
      // NavBar cuts off first element from view. Scroll to 'center' to fix this. All other elements scroll to 'start'
      if (id === 0) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      } else {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }
    },
    checkForPermision() {
      const userDecision = window.localStorage.getItem(cookiesKey);
      if (userDecision !== null) {
        const parsedUserDecision = JSON.parse(userDecision);
        if (parsedUserDecision) {
          return true;
        }
      }
      return false;
    },
  },
  mounted() {
    window.addEventListener('resize', this.onResize);
    this.analyticsPermitted = this.checkForPermision();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
};
</script>

<style scoped>
main {
  box-sizing: border-box;
  --heading-text-align: center;
  --page-title-f-size: 20px;
  --page-title-letter-spacing: 3px;
  --faq-spacing: 0.8em;
  --center-heading-height: 55%;
  --flex-height: 45%;
  --bullet-border-radius: 37px;
  --bullet-margin: 0.7em;
  --bullet-padding: 0 2em 0 2em;
  --bullet-min-width: 120px;
  --bullet-height: 42px;
  --flex-grow-width: 770px;
  --hero-color: white;
  --hero-font-weight: 400;
  --hero-font-size: 20px;
  --hero-height: 703px;
  --q-font-size: 70px;
  --hero-image-url: url(~@/assets/faq.jpg);
  --faq-container-f-size: 20px;
  --faq-container-color: #2b4047;
  --faq-title-f-weight: 800;
  --faq-title-f-size: 40px;
}

.PageTitle {
  letter-spacing: var(--page-title-letter-spacing);
  margin-bottom: 1.5em;
  text-transform: uppercase;
  font-size: var(--page-title-f-size);
}
.Question {
  font-size: var(--q-font-size);
  font-family: Foco;
  font-weight: bold;
}
article.HeroImage {
  height: var(--hero-height);
  width: 100%;
  max-width: 100vw;
  background-image: var(--hero-image-url);
  background-repeat: no-repeat;
  background-size: cover;
  color: var(--hero-color);
  font-weight: var(--hero-font-weight);
  font-size: var(--hero-font-size);
}

article.HeroImage .Bullet {
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: var(--bullet-min-width);
  height: var(--bullet-height);
  background: rgba(255, 255, 255, 0.4);
  border-radius: var(--bullet-border-radius);
  margin: var(--bullet-margin);
  padding: var(--bullet-padding);
  cursor: pointer;
}
.CenterHeading {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  height: var(--center-heading-height);
  text-align: var(--heading-text-align);
}
.Flex {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  height: var(--flex-height);
}
.FlexGrow {
  margin-top: 3.2em;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  flex-direction: row;
  width: var(--flex-grow-width);
}

.FAQContainer {
  font-size: var(--faq-container-f-size);
  color: var(--faq-container-color);
  min-height: auto;
}

.CenterFAQ {
  max-width: 1400px;
  margin: 0 auto;
}

.SectionTitle {
  font-weight: var(--faq-title-f-weight);
  font-size: var(--faq-title-f-size);
  font-family: Foco;
  margin-bottom: var(--faq-spacing);
}

.Line {
  width: 100%;
  height: 1px;
  background: #c8d1d3;
}

@media (max-width: 1540px) {
  .CenterFAQ {
    margin: 0 2em 0 2em;
  }
}

@media (max-width: 800px) {
  main {
    --q-font-size: 60px;
  }
}

@media (max-width: 600px) {
  main {
    --q-font-size: 55px;
    --page-title-f-size: 16px;
    --page-title-letter-spacing: 1.5px;
  }
}

@media (max-width: 550px) {
  main {
    --heading-text-align: left;
    --page-title-f-size: 12px;
    --bullet-border-radius: 25px;
    --bullet-margin: 0.5em 1em;
    --bullet-padding: 0 0.9em 0 0.9em;
    --bullet-min-width: 80px;
    --bullet-height: 35px;
    --hero-font-size: 14px;
    --hero-image-url: url(~@/assets/faq-mobile.jpg);
    --hero-height: 430px;
    --q-font-size: 30px;
    --center-heading-height: 45%;
    --flex-height: 55%;
    --faq-title-f-size: 30px;
  }
  article.HeroImage {
    background-repeat: no-repeat;
    background-size: cover;
    height: 470px;
  }
  .Bullet {
    max-width: 190px;
  }
  .FlexGrow > * {
    flex: 1 1 auto;
  }
  .HeadingContainer {
    max-width: 90%;
  }
}

@media (max-width: 360px) {
  .FlexGrow > * {
    flex: 0 1 auto;
  }
}
</style>

<style>
html {
  scroll-behavior: smooth;
}
</style>
